import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '../components/Card'
import { useOrganization } from '../contexts/OrganizationContext'
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { supabase } from '../services/supabase'
import {
  Users,
  FileText,
  Heart,
  Calendar,
  Archive,
  TrendingUp,
  Activity,
} from 'lucide-react'

const Dashboard = () => {
  const { t } = useTranslation()
  const { canRead, canWrite, loading, organization } = useOrganization()
  const { isDemo } = useSubscriptionStatus()
  const organizationId = useOrganizationId()
  const [statsData, setStatsData] = useState({
    clients: 0,
    carePlans: 0,
    therapies: 0,
    sessions: 0,
  })
  const [statsLoading, setStatsLoading] = useState(true)

  // Fetch actual counts for real users
  useEffect(() => {
    if (!organizationId || isDemo || loading) {
      setStatsLoading(false)
      return
    }

    const fetchStats = async () => {
      try {
        setStatsLoading(true)
        const [clientsResult, carePlansResult, therapiesResult, sessionsResult] = await Promise.all([
          supabase
            .from('clients')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', organizationId),
          supabase
            .from('care_plans')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', organizationId)
            .eq('status', 'active'),
          supabase
            .from('therapies')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', organizationId)
            .eq('status', 'scheduled'),
          supabase
            .from('sessions')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', organizationId),
        ])

        setStatsData({
          clients: clientsResult.count || 0,
          carePlans: carePlansResult.count || 0,
          therapies: therapiesResult.count || 0,
          sessions: sessionsResult.count || 0,
        })
      } catch (error) {
        console.error('Error fetching dashboard stats:', error)
      } finally {
        setStatsLoading(false)
      }
    }

    fetchStats()
  }, [organizationId, isDemo, loading])

  // Stats with permission checks
  const stats = useMemo(() => {
    const allStats = [
      {
        label: t('dashboard.stats.clients'),
        value: isDemo ? '24' : statsData.clients.toString(),
        icon: Users,
        color: 'from-primary-500 to-primary-600',
        link: '/clients',
        permission: { resource: 'clients', action: 'read' },
      },
      {
        label: t('dashboard.stats.carePlans'),
        value: isDemo ? '18' : statsData.carePlans.toString(),
        icon: Heart,
        color: 'from-secondary-500 to-secondary-600',
        link: '/care-plans',
        permission: { resource: 'care_plans', action: 'read' },
      },
      {
        label: t('dashboard.stats.therapies'),
        value: isDemo ? '12' : statsData.therapies.toString(),
        icon: Calendar,
        color: 'from-accent-500 to-accent-600',
        link: '/therapies',
        permission: { resource: 'therapies', action: 'read' },
      },
      {
        label: t('dashboard.stats.sessions'),
        value: isDemo ? '156' : statsData.sessions.toString(),
        icon: Archive,
        color: 'from-purple-500 to-purple-600',
        link: '/sessions',
        permission: { resource: 'sessions', action: 'read' },
      },
    ]

    // Filter stats based on read permissions
    if (loading) return []
    return allStats.filter((stat) => {
      if (!stat.permission) return true
      return canRead(stat.permission.resource)
    })
  }, [canRead, loading, isDemo, statsData, statsLoading])

  // Quick actions with permission checks
  const quickActions = useMemo(() => {
    const allActions = [
      {
        title: t('dashboard.quickActions.newMedicalHistory'),
        description: t('dashboard.quickActions.newMedicalHistoryDesc'),
        icon: FileText,
        link: '/examination',
        color: 'from-primary-400 to-primary-500',
        permission: { resource: 'clients', action: 'write' }, // Examinations require write access to clients
      },
      {
        title: t('dashboard.quickActions.createCarePlan'),
        description: t('dashboard.quickActions.createCarePlanDesc'),
        icon: Heart,
        link: '/care-plans',
        color: 'from-secondary-400 to-secondary-500',
        permission: { resource: 'care_plans', action: 'write' },
      },
      {
        title: t('dashboard.quickActions.scheduleTherapy'),
        description: t('dashboard.quickActions.scheduleTherapyDesc'),
        icon: Calendar,
        link: '/therapies',
        color: 'from-accent-400 to-accent-500',
        permission: { resource: 'therapies', action: 'write' },
      },
    ]

    // Filter actions based on write permissions
    if (loading) return []
    return allActions.filter((action) => {
      if (!action.permission) return true
      return canWrite(action.permission.resource)
    })
  }, [canWrite, loading])

  return (
    <div className="section-container">
      {/* Welcome Banner */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <Card variant="gradient" className="p-6 bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {t('dashboard.welcome')}{organization?.name ? ` ${organization.name}` : ''}!
              </h2>
              <p className="text-white/90">
                {organization?.name 
                  ? t('dashboard.welcomeMessage', { orgName: organization.name })
                  : t('dashboard.welcomeMessageNoOrg')}
              </p>
            </div>
            <div className="hidden md:block">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Activity size={32} className="text-white" />
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('dashboard.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400">{t('dashboard.overview')}</p>
      </motion.div>

      {/* Stats Grid */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statsLoading && !isDemo && (
            <div className="col-span-4 text-center text-gray-500 py-4">{t('dashboard.loadingStats')}</div>
          )}
          {stats.map((stat, index) => {
            const Icon = stat.icon
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={stat.link}>
                  <Card variant="soft" hover className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</p>
                      </div>
                      <div
                        className={`w-14 h-14 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}
                      >
                        <Icon size={24} className="text-white" />
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            )
          })}
        </div>
      )}

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('dashboard.quickActions.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, index) => {
              const Icon = action.icon
              return (
                <motion.div
                  key={action.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <Link to={action.link}>
                    <Card variant="gradient" hover className="p-6">
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-4 shadow-lg`}
                      >
                        <Icon size={24} className="text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {action.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
                    </Card>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        </motion.div>
      )}

      {/* Recent Activity - Only show for demo users */}
      {isDemo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card variant="soft" className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Activity size={24} className="text-primary-600 dark:text-primary-400" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.recentActivity')}</h2>
            </div>
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-4 p-3 rounded-xl bg-white/50 dark:bg-gray-700/50 hover:bg-white/80 dark:hover:bg-gray-700/80 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-400 to-secondary-400 flex items-center justify-center">
                    <TrendingUp size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {t('dashboard.recentActivityItem', { clientName: 'Sarah Johnson' })}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('dashboard.recentActivityTime', { hours: 2 })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  )
}

export default Dashboard

